import React, { useCallback, useState } from 'react';
import { Button } from 'antd';
import ViewNotes from '../../../ManageNodes/ViewNotes';
import type { OrderDetails } from '../../types';

interface InlineNotesPanelProps {
  /** The order whose notes to show (provides pacs_order.patient). */
  details: OrderDetails;
  /** Which section to render — driven by the parent's active tab. */
  section: 'his' | 'ris';
}

/**
 * Inline (non-modal) host for <ViewNotes>, used by the reporting-page HIS/RIS tabs.
 * Mirrors ViewNotesModal's note-selection / edit / save wiring, but instead of the
 * modal's OK button it shows a small Save/Cancel bar only while a text note is being
 * edited. A single instance stays mounted across the HIS/RIS tabs (the `section` prop
 * swaps which part renders), so switching tabs never refetches.
 */
const InlineNotesPanel: React.FC<InlineNotesPanelProps> = ({ details, section }) => {
  const [selectedNote, setSelectedNote] = useState<any>({});
  const [noteEditActive, setNoteEditActive] = useState(false);
  const [saveNoteHandler, setSaveNoteHandler] = useState<null | (() => Promise<boolean> | boolean)>(null);
  const [saving, setSaving] = useState(false);

  const handleNoteSelection = useCallback((note: any) => {
    setSelectedNote(prev => {
      const prevId = prev?.rn_id || null;
      const nextId = note?.rn_id || null;
      const prevText = prev?.rn_notes || '';
      const nextText = note?.rn_notes || '';
      if (prevId === nextId && prevText === nextText) return prev;
      return note || {};
    });
  }, []);

  const handleSave = async () => {
    if (!saveNoteHandler) return;
    setSaving(true);
    try {
      await saveNoteHandler();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="inline-notes-panel">
      {noteEditActive && (
        <div className="inline-notes-editbar">
          <Button size="small" onClick={() => setNoteEditActive(false)}>Cancel</Button>
          <Button size="small" type="primary" loading={saving} onClick={handleSave}>Save</Button>
        </div>
      )}
      <div className="inline-notes-body">
        <ViewNotes
          section={section}
          handleNoteSelection={handleNoteSelection}
          selectedNote={selectedNote}
          viewNotesModal={{ visible: true, details }}
          isEditMode={noteEditActive}
          onEditNoteStateChange={setNoteEditActive}
          onSaveNoteRegister={setSaveNoteHandler}
        />
      </div>
    </div>
  );
};

export default InlineNotesPanel;
